// Duplicate detection for imported positions.
//
// Lifted out of ChatAssistant.tsx so any import flow can use the same rule. The
// business key is: trade date, volume, price, counterparty, budget month/year,
// contract month/year, side and commodity. A row matching an existing position on
// all ten is considered already uploaded and is skipped.
//
// Note this is NOT the same thing as PositionAggregationHelper's getGroupKey, which
// groups by commodity/budget date/location for reporting. Different purpose, different
// dimensions - don't merge them.
import dayjs from 'dayjs';
import { ContractDate, PositionModel, Side, Unit } from '../Generated/Commodity-Management-Api';

/**
 * The subset of a parsed import row the duplicate check needs. Deliberately a flat,
 * client-agnostic shape rather than any one importer's row type.
 */
export interface DuplicateCheckPosition {
    tradeDate?: string | null;
    volume: number;
    price: number;
    unit?: Unit;
    side?: Side;
    commodityId?: number;
    counterpartyId?: number;
    budgetDate?: ContractDate;
    contractDate?: ContractDate;
}

// Volume tolerances absorb unit-conversion rounding; price tolerances absorb float
// drift between a file value (4.6525) and a stored one (4.65249999).
const VOLUME_ABSOLUTE_TOLERANCE = 1e-3;
const VOLUME_RELATIVE_TOLERANCE = 1e-4;
const PRICE_ABSOLUTE_TOLERANCE = 1e-4;
const PRICE_RELATIVE_TOLERANCE = 1e-5;

/** Normalize a date-ish value to YYYY-MM-DD, or undefined when unparseable. */
function normalizeDate(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
}

function contractDatesMatch(a: ContractDate | undefined, b: ContractDate | undefined): boolean {
    return (a?.month ?? null) === (b?.month ?? null) && (a?.year ?? null) === (b?.year ?? null);
}

function withinTolerance(a: number, b: number, absolute: number, relative: number): boolean {
    return Math.abs(a - b) <= Math.max(absolute, Math.abs(b) * relative);
}

/**
 * Volumes may be expressed in different units between the file (often contracts) and
 * the database (the commodity's display unit, e.g. bushels). Convert both onto the
 * existing position's basis using its contract volume before comparing.
 */
function toCommonBasis(volume: number, unit: Unit | undefined, contractVolume: number | null): number | null {
    if (volume === undefined || volume === null || isNaN(Number(volume))) return null;
    if (unit === Unit.Contract && contractVolume) {
        return Number(volume) * contractVolume;
    }
    return Number(volume);
}

/**
 * True when the candidate row matches an existing position on the full business key.
 * Any field missing on either side is treated as "not a match" rather than a wildcard -
 * skipping an upload is worse than allowing a near-duplicate through for review.
 */
export function isDuplicatePosition(candidate: DuplicateCheckPosition, existing: PositionModel): boolean {
    const candidateTradeDate = normalizeDate(candidate.tradeDate);
    const existingTradeDate = normalizeDate(existing.tradeDate);
    if (!candidateTradeDate || !existingTradeDate || candidateTradeDate !== existingTradeDate) return false;

    if (candidate.side === undefined || candidate.side !== existing.side) return false;

    if (candidate.commodityId === undefined || candidate.commodityId !== existing.commodity?.commodityId) return false;

    if (candidate.counterpartyId === undefined || candidate.counterpartyId !== existing.counterparty?.counterpartyId) {
        return false;
    }

    if (!contractDatesMatch(candidate.budgetDate, existing.budgetDate)) return false;
    if (!contractDatesMatch(candidate.contractDate, existing.contractDate)) return false;

    const contractVolume = existing.positionPricingStrategy?.contractVolume ?? null;
    const candidateVolume = toCommonBasis(candidate.volume, candidate.unit, contractVolume);
    const existingVolume = toCommonBasis(existing.volume, existing.unit, contractVolume);
    if (candidateVolume === null || existingVolume === null) return false;
    if (!withinTolerance(candidateVolume, existingVolume, VOLUME_ABSOLUTE_TOLERANCE, VOLUME_RELATIVE_TOLERANCE)) {
        return false;
    }

    const candidatePrice = Number(candidate.price);
    const existingPrice = Number(existing.price);
    if (isNaN(candidatePrice) || isNaN(existingPrice)) return false;
    if (!withinTolerance(candidatePrice, existingPrice, PRICE_ABSOLUTE_TOLERANCE, PRICE_RELATIVE_TOLERANCE)) {
        return false;
    }

    return true;
}

/** Returns the first existing position the candidate duplicates, if any. */
export function findDuplicatePosition(
    candidate: DuplicateCheckPosition,
    existingPositions: PositionModel[] | undefined
): PositionModel | undefined {
    if (!existingPositions || existingPositions.length === 0) return undefined;
    return existingPositions.find(existing => isDuplicatePosition(candidate, existing));
}
